import React from 'react';
import type { SearchParams } from '@/server/searchParams.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import type { AutocompleteHotelResponse } from '@/app/components/search/utils/api/autocompleteHotelResponseTypes.ts';
import type { APIStatusEnum } from '@/app/components/search/common/reloadData/APIStatusEnum.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { removeParameters } from '@/app/common/urlParametersUtils.ts';
import Autocomplete, { type AutoCompleteBasicProps, type Suggestions } from '../../../../AutocompleteBasic';
import type { CuriosityParams } from '../../../../../Curiosity';
import type { Localization } from '../../../../../common/Localization';
import { createSuggestion } from './utils';
import clientAPICall from '../../../../../common/clientAPICall.ts';

export type AutocompleteHotelPayload = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  commonData: CommonData;
  localization: Localization;
  curiosityParams: CuriosityParams;
};

export type AutocompleteHotelAPIFullResponse = {
  status: APIStatusEnum;
  autocompleteHotels?: AutocompleteHotelResponse;
};

export type PropertyNameSelected = {
  target: {
    name: string;
    value: string;
  };
};

type Props = {
  searchParams: SearchParams;
  name: string;
  onUpdate: (e: PropertyNameSelected) => void;
  value: string;
  isMobile: boolean;
};

export const PropertyNameSelector = ({ searchParams, name, onUpdate, value, isMobile }: Props) => {
  const { commonData, searchInfoData, localization, curiosityParams } = usePageWrapperData();
  const { t } = useTranslation();

  const initialValue = value || '';
  const propertyPlaceholder = t('vacation.oss.filters.property.placeholder');
  const propertyNameNoResults = t('vacation.oss.components.autocomplete.other.notFound');

  const suggestionFetcher = async (query: string): Promise<Suggestions> => {
    const newQueryString = {
      ...removeParameters(searchParams, [URLParameters.MAP_AREA]),
      [URLParameters.ACCOMODATION_NAME]: query,
    };

    const payload: AutocompleteHotelPayload = {
      searchParams: newQueryString,
      searchInfoData,
      commonData,
      localization,
      curiosityParams,
    };
    const suggestionsRaw = await clientAPICall<AutocompleteHotelPayload, AutocompleteHotelAPIFullResponse>({
      apiName: API_NAME.AUTOCOMPLETE_HOTEL,
      payload,
      commonData,
      traceParent: searchInfoData.searchTraceParent,
    });

    const suggestions = (suggestionsRaw?.autocompleteHotels || [])
      .map((s) => ({ ...s, id: `${s.id}` }))
      .map(createSuggestion);

    return Promise.resolve(suggestions);
  };

  const handleSelected = (chosenValue: string) => {
    onUpdate({ target: { name, value: chosenValue } });
  };

  const autocompleteProps = {
    suggestionFetcher,
    placeholder: propertyPlaceholder,
    initialValue,
    noSuggestionsLabel: propertyNameNoResults,
    isFullScreen: isMobile,
    focusOnLoad: isMobile,
    onSelected: handleSelected,
  } as AutoCompleteBasicProps;

  return <Autocomplete {...autocompleteProps} />;
};
